<template>
    <div class="m-3" style="height: 90vh">
      <ReportsTable
          ref="rtable"
          :columns="columnDefs"
          :settings="{ theme: 'ag-theme-alpine', hideTable: true, title: t('registerClosingSummary') }"
          endpoint="reports/accounting/register-closing"
          @onDataLoaded="onDataLoaded"
      >
        <template #filter>
          <el-select
              filterable
              clearable
              class="col-span-12 md:col-span-3"
              value-key="id"
              :placeholder="t('selectRegister')"
              v-model="selectedRegister"
              @change="onSelectedRegisterChanged"
              remote
              :remote-method="searchRegister"
          >
            <el-option
                v-for="option in registers"
                :key="option.id"
                :label="$ctx.locale == 'en' ? option.enName : option.arName"
                :value="option"
            ></el-option>
          </el-select>
        </template>
        <template #cards="{data}: {data: any[]}">
          <div class="flex flex-col gap-4">
            <template v-if="data">
              <div v-for="i in data" :key="i.id"
                   class="bg-white rounded-lg shadow-md p-5 grid text-xl"
                   style="grid-template-columns: 40% 60%">
                <div class="grid grid-cols-2">
                  <div>{{ t('closingDate') }}</div>
                  <div>{{ t('by') }}</div>
                  <div class="font-bold rtl:text-end" dir='ltr'>{{
                      formatPostgresDate(i?.closingDate, 'DD-MM-YYYY hh:mm A')
                    }}
                  </div>
                  <div class="font-bold">{{ i?.closedBy?.name }}</div>
                  <div>{{ t('openingDate') }}</div>
                  <div>{{ t('by') }}</div>
                  <div class="font-bold rtl:text-end" dir='ltr'>{{
                      formatPostgresDate(i?.openingDate, 'DD-MM-YYYY hh:mm A')
                    }}
                  </div>
                  <div class="font-bold">{{ i?.openedBy?.name }}</div>
                </div>
                <div class="border-s border-gray-300 grid grid-cols-4 ps-10">
                  <div></div>
                  <div>{{ t('expected') }}</div>
                  <div>{{ t('deposited') }}</div>
                  <div>{{ t('difference') }}</div>
  
                  <div>{{ t('floating') }}</div>
                  <div class="font-bold p-1">{{ formatMoney(i?.expectedClosingFloatCash, $ctx.currency).replaceAll('&nbsp;', '').replace('IQD', '').replace('$', '') }}</div>
                  <div class="font-bold bg-blue-200 rounded-md m-1 ps-1">{{ formatMoney(i?.actualClosingFloatCash, $ctx.currency).replaceAll('&nbsp;', '').replace('IQD', '').replace('$', '') }}</div>
                  <div class="font-bold p-1" :class="{'text-green-700': i?.floatCashVariance > 0, 'text-red-500': i?.floatCashVariance < 0}">{{ addPlus(i?.floatCashVariance) + formatMoney(i?.floatCashVariance, $ctx.currency).replaceAll('&nbsp;', '').replace('IQD', '').replace('$', '') }}</div>
  
                  <div>IQD</div>
                  <div class="font-bold p-1">{{formatMoney(i?.expectedDepositedCash, 'IQD').replaceAll('&nbsp;', '').replace('IQD', '')}}</div>
                  <div class="font-bold bg-blue-200 rounded-md m-1 ps-1">{{formatMoney(i?.actualDepositedCash, 'IQD').replaceAll('&nbsp;', '').replace('IQD', '')}}</div>
                  <div class="font-bold p-1" :class="{'text-green-700': i?.depositedCashVariance > 0, 'text-red-500': i?.depositedCashVariance < 0}">{{addPlus(i?.depositedCashVariance) + formatMoney(i?.depositedCashVariance, 'IQD').replaceAll('&nbsp;', '').replace('IQD', '')}}</div>
  
                  <div>USD</div>
                  <div class="font-bold p-1">{{formatMoney(i?.foreignCurrencyExpectedDepositedCash, 'USD').replaceAll('&nbsp;', '').replace('$', '')}}</div>
                  <div class="font-bold bg-blue-200 rounded-md m-1 ps-1">{{formatMoney(i?.foreignCurrencyActualDepositedCash, 'USD').replaceAll('&nbsp;', '').replace('$', '')}}</div>
                  <div class="font-bold p-1" :class="{'text-green-700': i?.foreignCurrencyDepositedCashVariance > 0, 'text-red-500': i?.foreignCurrencyDepositedCashVariance < 0}">{{ addPlus(i?.foreignCurrencyDepositedCashVariance) + formatMoney(i?.foreignCurrencyDepositedCashVariance, 'USD').replaceAll('&nbsp;', '').replace('$', '')}}</div>
  
                  <div>E-Pay</div>
                  <div class="font-bold p-1">{{formatMoney(i?.ePaymentBalanceAmount, 'IQD').replaceAll('&nbsp;', '').replace('IQD', '')}}</div>
                  <div class="font-bold p-1">-</div>
                  <div class="font-bold p-1">-</div>
                </div>
              </div>
            </template>
          </div>
        </template>
      </ReportsTable>
    </div>
  </template>
  
  <script setup lang="ts">
  import {onBeforeMount, ref} from 'vue'
  import {useI18n} from 'vue-i18n'
  import {ColDef} from 'ag-grid-community'
  import {useRoute, useRouter} from 'vue-router'
  import {useHTTP} from '@/plugins/http'
  import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
  import {formatMoney} from '@/utils/money'
  import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
  import {formatPostgresDate} from '@/utils/date'
  import {Register} from '@/models/company/Register'
  import {useContext} from '@/plugins/context'
  
  // i18n
  const {t} = useI18n({
    messages: {
      en: {
        registerClosingSummary: 'Register Closing Summary',
        name: 'Name',
        selectRegister: 'Select Register',
        ePaymentBalance: 'E-Payment Balance',
        depositedUSDDifference: 'Deposited USD Difference',
        actualDepositedUSD: 'Actual Deposited USD',
        expectedDepositedUSD: 'Expected Deposited USD',
        depositedCashDifference: 'Deposited Cash Difference',
        overallIQDCash: 'Overall IQD Cash +/-',
        actualDepositedCash: 'Actual Deposited Cash',
        expectedDepositedCash: 'Expected Deposited Cash',
        closingFloatDifference: 'Closing Float Difference',
        openingFloatDifference: 'Opening Float Difference',
        closingDate: 'Closing Date',
        closedBy: 'Closed By',
        openingDate: 'Opening Date',
        openedBy: 'Opened By',
        register: 'Register',
  
        floating: 'Floating',
        closingOn: 'Closing On',
        by: 'By',
        openedOn: 'Opened On',
        expected: 'Expected',
        deposited: 'Deposited',
        difference: 'Difference',
        registerClosingSummary: 'Register Closing Summary'
      },
      ar: {
        registerClosingSummary: 'ملخص إغلاق الصندوق',
        name: 'الاسم',
        selectRegister: 'اختر الصندوق',
        ePaymentBalance: 'صافي النقد الالكتروني',
        depositedUSDDifference: 'الفرق في USD',
        actualDepositedUSD: 'مبلغ التوديع الفعلي USD',
        expectedDepositedUSD: 'مبلغ التوديع المتوقع USD',
        depositedCashDifference: 'فرق مبلغ التوديع',
        overallIQDCash: 'الفرق الاجمالي في IQD',
        actualDepositedCash: 'مبلغ التوديع الفعلي',
        expectedDepositedCash: 'مبلغ التوديع المتوقع',
        closingFloatDifference: 'فرق المبلغ الدوار عن الغلق',
        openingFloatDifference: 'فرق المبلغ الدوار عند الفتح',
        closingDate: 'تاريخ الاغلاق',
        closedBy: 'غلق الصندوق',
        openingDate: 'تاريخ الفتح',
        openedBy: 'فتح الصندوق',
        register: 'الصندوق',
  
        floating: 'الدوار',
        closingOn: 'اغلق في',
        by: 'المسؤول',
        openedOn: 'فتح في',
        expected: 'المتوقع',
        deposited: 'المودّع',
        difference: 'الفروقات',
        registerClosingSummary: 'ملخص صندوق النقد'
      }
    }
  })
  
  const ctx = useContext()
  
  // Data
  const rtable = ref<any>()
  const columnDefs = ref<ColDef[]>([])
  
  const registers = ref<Register[] | any>()
  const selectedRegister = ref()
  
  const http = useHTTP()
  const route = useRoute()
  const router = useRouter()
  const alertModal = useAlertModal()
  
  onBeforeMount(async () => {
    columnDefs.value = [
      {
        headerName: t('register'),
        field: 'registerID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        valueFormatter: ({data}) => {
          if (ctx.locale == 'ar') {
            return `${data.register.arName}`
          }
          return `${data.register.enName}`
        }
      },
      {
        headerName: t('openedBy'),
        field: 'openedByID',
        valueFormatter: ({data}) => {
          return `${data.openedBy.name}`
        }
      },
      {
        headerName: t('openingDate'),
        field: 'openingDate',
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => {
          return `<div dir='ltr'>${formatPostgresDate(params.value, 'DD-MM-YYYY hh:mm:ss A')}</div>`
        }
      },
      {
        headerName: t('closedBy'),
        field: 'closedByID',
        valueFormatter: ({data}) => {
          return `${data.closedBy.name}`
        }
      },
      {
        headerName: t('closingDate'),
        field: 'closingDate',
        filter: 'agDateColumnFilter',
        cellRenderer: (params) => {
          return `<div dir='ltr'>${formatPostgresDate(params.value, 'DD-MM-YYYY hh:mm:ss A')}</div>`
        }
      },
      {
        headerName: t('openingFloatDifference'),
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.actualOpeningFloatCash - data.expectedOpeningFloatCash, 'IQD').replace('IQD', '')}`
        },
        cellStyle: ({data}) => {
          const base = {}
          const value = data.actualOpeningFloatCash - data.expectedOpeningFloatCash
          if (value < 0) {
            base['color'] = 'red'
          } else if (value > 0) {
            base['color'] = 'green'
          }
  
          return base
        }
      },
      {
        headerName: t('closingFloatDifference'),
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.actualClosingFloatCash - data.expectedClosingFloatCash, 'IQD').replace('IQD', '')}`
        },
        cellStyle: ({data}) => {
          const base = {}
          const value = data.actualClosingFloatCash - data.expectedClosingFloatCash
          if (value < 0) {
            base['color'] = 'red'
          } else if (value > 0) {
            base['color'] = 'green'
          }
  
          return base
        }
      },
      {
        headerName: t('expectedDepositedCash'),
        field: 'expectedDepositedCash',
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.expectedDepositedCash, 'IQD').replace('IQD', '')}`
        }
      },
      {
        headerName: t('actualDepositedCash'),
        field: 'actualDepositedCash',
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.actualDepositedCash, 'IQD').replace('IQD', '')}`
        }
      },
      {
        headerName: t('depositedCashDifference'),
        field: 'depositedCashVariance',
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.depositedCashVariance, 'IQD').replace('IQD', '')}`
        },
        cellStyle: ({data}) => {
          const base = {}
          const value = data.depositedCashVariance
          if (value < 0) {
            base['color'] = 'red'
          } else if (value > 0) {
            base['color'] = 'green'
          }
  
          return base
        }
      },
      {
        headerName: t('overallIQDCash'),
        minWidth: 250,
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney((data.actualOpeningFloatCash - data.expectedOpeningFloatCash) + (data.actualClosingFloatCash - data.expectedClosingFloatCash) + data.depositedCashVariance, 'IQD').replace('IQD', '')}`
        },
        cellStyle: ({data}) => {
          const base = {}
          const value = (data.actualOpeningFloatCash - data.expectedOpeningFloatCash) + (data.actualClosingFloatCash - data.expectedClosingFloatCash) + data.depositedCashVariance
          if (value < 0) {
            base['color'] = 'red'
          } else if (value > 0) {
            base['color'] = 'green'
          }
  
          return base
        }
      },
      {
        headerName: t('expectedDepositedUSD'),
        field: 'foreignCurrencyExpectedDepositedCash',
        minWidth: 250,
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.foreignCurrencyExpectedDepositedCash, 'USD').replace('USD', '')}`
        }
      },
      {
        headerName: t('actualDepositedUSD'),
        field: 'foreignCurrencyActualDepositedCash',
        minWidth: 250,
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.foreignCurrencyActualDepositedCash, 'USD').replace('USD', '')}`
        }
      },
      {
        headerName: t('depositedUSDDifference'),
        field: 'foreignCurrencyDepositedCashVariance',
        minWidth: 250,
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.foreignCurrencyDepositedCashVariance, 'USD').replace('USD', '')}`
        },
        cellStyle: ({data}) => {
          const base = {}
          const value = data.foreignCurrencyDepositedCashVariance
          if (value < 0) {
            base['color'] = 'red'
          } else if (value > 0) {
            base['color'] = 'green'
          }
  
          return base
        }
      },
      {
        headerName: t('ePaymentBalance'),
        field: 'ePaymentBalanceAmount',
        minWidth: 250,
        valueFormatter: ({data}) => {
          if (!data) return ''
          return `${formatMoney(data.ePaymentBalanceAmount, 'IQD').replace('IQD', '')}`
        }
      }
    ]
  })
  
  function onDataLoaded(data: any) {
  }
  
  async function onSelectedRegisterChanged(event: any) {
    rtable.value.payload.filter = selectedRegister.value ? selectedRegister.value.id : null
    rtable.value.navigate(rtable.value.payload)
  }
  
  async function searchRegister(query: string) {
    if (!query) {
      query = ''
    }
    try {
      const url = `${Register.ENDPOINT}?q=${query}`
      const res = await http.get<Register[]>(url)
      registers.value = res.map((register) => Register.from(register))
    } catch (error) {
      registers.value = []
    }
  }
  
  function addPlus(value: number): string {
    if (value > 0) {
      return "+";
    }
    return '';
  }
  </script>
  